import React from 'react';
import { Autocomplete } from '@mui/material';
import type { AutocompleteChangeReason } from '@mui/material/useAutocomplete';

import type { AutocompleteCommonProps } from './field.types';
import { FieldShell } from './FieldShell';
import { useFieldIds } from './useFieldIds';
import { useAutocompleteField } from './useAutocompleteField';
import { autocompleteSx } from './fieldStyles';
import { AutocompleteStatus, AutocompleteTextBox, optionRenderer } from './AutocompleteParts';

export interface AutocompleteInputProps<T> extends AutocompleteCommonProps<T> {
  readonly value: T | null;
  readonly onChange: (value: T | null, event: React.SyntheticEvent) => void;
}

export const AutocompleteInput = <T,>(props: AutocompleteInputProps<T>) => {
  const {
    id: idProp, label, hideLabel, helperText, describedById, error, required, hideRequiredMark, disabled = false, size = 'small', fullWidth = true, className, sx,
    options, value, onChange, getOptionKey, getOptionLabel, placeholder, loading = false, loadingText, noOptionsText, onSearch, searchDelayMs, onOpen, onClose,
    readOnly = false, clearable = true, renderOption,
  } = props;
  const ids = useFieldIds(idProp);
  const interactive = !disabled && !readOnly;
  const field = useAutocompleteField<T>({
    options,
    selected: value === null ? [] : [value],
    getOptionKey,
    getOptionLabel,
    filterSelectedOptions: false,
    interactive,
    loading,
    loadingText,
    noOptionsText,
    onSearch,
    searchDelayMs,
    onOpen,
    onClose,
  });

  const handleChange = (event: React.SyntheticEvent, next: T | null, reason: AutocompleteChangeReason) => {
    if (reason !== 'selectOption' || next === null) return;
    field.finish();
    if (value !== null && getOptionKey(value) === getOptionKey(next)) return;
    onChange(next, event);
  };

  const clear = (event: React.MouseEvent) => {
    onChange(null, event);
    field.restart();
  };

  const shownText = value === null ? '' : getOptionLabel(value);

  return (
    <FieldShell ids={ids} label={label} hideLabel={hideLabel} helperText={helperText} error={error} required={required} hideRequiredMark={hideRequiredMark} disabled={disabled} size={size} fullWidth={fullWidth} className={className} sx={sx}>
      <Autocomplete<T, false, boolean, false>
        {...field.listProps}
        id={ids.id}
        value={value}
        onChange={handleChange}
        inputValue={field.draft ?? shownText}
        size={size}
        disabled={disabled}
        readOnly={readOnly}
        sx={autocompleteSx}
        renderOption={optionRenderer(getOptionLabel, renderOption)}
        renderInput={(params) => (
          <AutocompleteTextBox
            params={params}
            ids={ids}
            helperText={helperText}
            describedById={describedById}
            placeholder={placeholder}
            size={size}
            busy={field.busy}
            onClear={clearable && interactive && value !== null ? clear : null}
            {...field.textBoxHandlers}
          />
        )}
      />
      <AutocompleteStatus>{field.status}</AutocompleteStatus>
    </FieldShell>
  );
};
